import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useContext,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

import { isSameDay, format } from "date-fns";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ErrorOutline,
  ExpandMore,
  GetApp,
  Replay,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import NewTicketModal from "../NewTicketModal";
import QueueMenuOptions from "../QueueMenuOptions";
import whatsBackground from "../../assets/wa-background.png";
import LocationPreview from "../LocationPreview";
import VcardPreview from "../VcardPreview";

import whatsBackgroundDark from "../../assets/wa-background-dark.png"; //DARK MODE PLW DESIGN//

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getMessageDate } from "../../utils/messageDate";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  messagesList: {
    backgroundImage:
      theme.mode === "light"
        ? `url(${whatsBackground})`
        : `url(${whatsBackgroundDark})`, //DARK MODE PLW DESIGN//
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  reactionContainer: {
    position: "absolute",
    bottom: -14,
    left: 8,
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 8px",
    borderRadius: 12,
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
    fontSize: 13,
  },
  reactionContainerRight: {
    left: 8,
    right: "auto",
    backgroundColor: "#dcf8c6",
  },
  // Reserva espaço suficiente no lado de baixo para o footer + reações
  messageWithReactions: {
    marginBottom: 26,
  },
  reactionBadge: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  reactionCount: {
    fontSize: 11,
    color: "rgba(0, 0, 0, 0.6)",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  externalFromMeHint: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.58)",
  },

  forwardedBadge: {
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.55)",
    marginBottom: 4,
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    lineHeight: 1.1,
  },

  timestampTimeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    whiteSpace: "nowrap",
  },
  timestampAckRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    whiteSpace: "nowrap",
  },

  // Dá espaço para 2 linhas do footer (hora + status/ack) sem colidir com texto
  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 22px 6px",
  },
  textContentItemEdited: {
    overflowWrap: "break-word",
    padding: "3px 120px 22px 6px",
  },
  editedBodyStack: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  editedSignature: {
    marginBottom: 2,
  },
  deletedOwnContent: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "rgba(0, 0, 0, 0.74)",
    fontStyle: "normal",
    opacity: 1,
    filter: "none",
  },
  deletedOwnIcon: {
    color: "rgba(0, 0, 0, 0.74)",
    opacity: 1,
  },
  editedOriginalBody: {
    opacity: 0.45,
    filter: "blur(0.45px)",
    color: "rgba(0, 0, 0, 0.68)",
    userSelect: "none",
    pointerEvents: "none",
    transform: "translateZ(0)",
  },
  editedFinalBody: {
    opacity: 1,
  },
  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 22px 6px",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
  documentMetadata: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "2px 6px 6px",
  },
  documentName: {
    fontWeight: 500,
    wordBreak: "break-word",
  },
  documentDetails: {
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.6)",
  },
  interactiveContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 6,
  },
  interactiveButton: {
    textTransform: "none",
    justifyContent: "flex-start",
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
  listRow: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    padding: "6px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: "none",
    textAlign: "left",
    cursor: "pointer",
  },
  listRowTitle: {
    fontWeight: 500,
  },
  listRowDescription: {
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.6)",
  },
  deliveryStatusText: {
    fontSize: 11,
    opacity: 0.85,
  },
  ackStatusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  resendButton: {
    marginLeft: 4,
    padding: 2,
  },
  resendIcon: {
    fontSize: 14,
  },
}));

const reducer = (state, action) => {
  const findCorrelationIndex = (message) => {
    if (!message) return -1;

    const normalizedMessageId = String(message.messageId ?? message.id ?? "");

    return state.findIndex((storedMessage) => {
      if (!storedMessage) return false;

      const storedMessageId = String(
        storedMessage.messageId ?? storedMessage.id ?? "",
      );

      return (
        storedMessage.id === message.id ||
        storedMessageId === normalizedMessageId ||
        storedMessage.id === `recovery:${normalizedMessageId}` ||
        message.id === `recovery:${storedMessageId}`
      );
    });
  };

  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = findCorrelationIndex(newMessage);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = findCorrelationIndex(messageToUpdate);

    if (messageIndex !== -1) {
      state[messageIndex] = { ...state[messageIndex], ...messageToUpdate };
    }

    return [...state];
  }

  if (action.type === "REMOVE_MESSAGE") {
    const idToRemove = String(action.payload || "");
    if (!idToRemove) return [...state];
    return state.filter((message) => String(message?.id || "") !== idToRemove);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const RECOVERY_NOTIFICATION_TIMEOUT_MS = 3 * 60 * 1000;

const getRecoveryMessageId = (payload = {}) =>
  String(payload.messageId || payload.id || "").trim();

const MessagesList = ({
  ticket,
  ticketId,
  isGroup,
  syncCount,
  allowMessageEditing = true,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const FORWARDED_MANY_TIMES_THRESHOLD = 5;

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  const socketManager = useContext(SocketContext);
  const { user: currentUser } = useContext(AuthContext);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [vcardInitialContact, setVcardInitialContact] = useState(null);
  const [resendingByMessageId, setResendingByMessageId] = useState({});
  const activeRecoveryByMessageIdRef = useRef(new Map());
  const joinChatBoxRetriesRef = useRef({});

  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return;

    const showSub = Keyboard.addListener("keyboardDidShow", (e) => {
      setKeyboardOffset(e.keyboardHeight || 0);
    });
    const hideSub = Keyboard.addListener("keyboardDidHide", () => {
      setKeyboardOffset(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  useEffect(() => {
    activeRecoveryByMessageIdRef.current.forEach((entry) => {
      if (entry?.timeoutId) {
        clearTimeout(entry.timeoutId);
      }
    });
    activeRecoveryByMessageIdRef.current.clear();
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [syncCount]);

  useEffect(() => {
    return () => {
      activeRecoveryByMessageIdRef.current.forEach((entry) => {
        if (entry?.timeoutId) {
          clearTimeout(entry.timeoutId);
        }
      });
      activeRecoveryByMessageIdRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    const activeTicketId = ticketId;
    
    // ============================================================
    // TASK 3: Frontend Auto-JoinChatBox com Retry
    // - Emite joinChatBox quando activeTicketId muda
    // - Retry automático (3x) se falhar
    // - Validação de resposta do server
    // - Log estruturado
    // ============================================================
    if (activeTicketId) {
      let retryCount = 0;
      const maxRetries = 3;
      
      const attemptJoinChatBox = () => {
        socket.emit("joinChatBox", `${activeTicketId}`, (err) => {
          if (err) {
            retryCount++;
            console.error(
              `[MessagesList] Erro ao entrar na conversa ${activeTicketId}: ${err.message || err}. ` +
              `Tentativa ${retryCount}/${maxRetries}`
            );
            
            if (retryCount < maxRetries) {
              // Retry com backoff exponencial: 1s, 2s, 4s
              const delay = Math.pow(2, retryCount - 1) * 1000;
              setTimeout(attemptJoinChatBox, delay);
            } else {
              console.error(
                `[MessagesList] Falha permanente ao entrar na conversa ${activeTicketId} ` +
                `após ${maxRetries} tentativas`
              );
            }
          } else {
            console.log(`[✅ MessagesList] Conectado com sucesso a conversa: ${activeTicketId}`);
            retryCount = 0;
          }
        });
      };
      
      attemptJoinChatBox();
    }

    const clearRecoveryPlaceholder = (messageId) => {
      if (!messageId) return;
      const entry = activeRecoveryByMessageIdRef.current.get(messageId);
      if (entry?.timeoutId) {
        clearTimeout(entry.timeoutId);
      }
      activeRecoveryByMessageIdRef.current.delete(messageId);

      if (entry?.placeholderId) {
        dispatch({ type: "REMOVE_MESSAGE", payload: entry.placeholderId });
      }
    };

    const registerRecoveryPlaceholder = (messagePayload) => {
      const messageId = getRecoveryMessageId(messagePayload);
      if (!messageId) return;

      const existing = activeRecoveryByMessageIdRef.current.get(messageId);
      if (existing?.timeoutId) {
        clearTimeout(existing.timeoutId);
      }

      const placeholderId = `recovery:${messageId}`;
      const timeoutId = setTimeout(() => {
        dispatch({ type: "REMOVE_MESSAGE", payload: placeholderId });
        activeRecoveryByMessageIdRef.current.delete(messageId);
      }, RECOVERY_NOTIFICATION_TIMEOUT_MS);

      activeRecoveryByMessageIdRef.current.set(messageId, {
        placeholderId,
        timeoutId,
      });

      dispatch({
        type: "ADD_MESSAGE",
        payload: {
          ...messagePayload,
          id: placeholderId,
          messageId,
          body:
            messagePayload.body || i18n.t("notifications.recovery.inProgress"),
          isRecoveryPlaceholder: true,
        },
      });
    };
    const handleAppMessage = (data) => {
      const payloadMessage = data.message || {};
      const payloadTicketId = payloadMessage.ticketId || data.ticketId || null;
      const normalizedRecoveryMessage = {
        ...payloadMessage,
        messageId:
          payloadMessage.messageId || payloadMessage.id || data.messageId,
        ticketId: payloadMessage.ticketId || data.ticketId,
        contactId: payloadMessage.contactId || data.contactId,
        contactName: payloadMessage.contactName || data.contactName,
        reason: payloadMessage.reason || data.reason,
        status: payloadMessage.status || data.status,
      };
      const correlationId =
        getRecoveryMessageId(normalizedRecoveryMessage) ||
        String(data.messageId || "");

      if (
        data.action === "incoming_recovery" &&
        Number(payloadTicketId) === Number(currentTicketId.current)
      ) {
        registerRecoveryPlaceholder(normalizedRecoveryMessage);
      }

      if (
        (data.action === "create" ||
          data.action === "incoming_recovery_resolved") &&
        correlationId
      ) {
        clearRecoveryPlaceholder(correlationId);
      }

      if (
        data.action === "create" &&
        Number(payloadTicketId) === Number(currentTicketId.current)
      ) {
        dispatch({
          type: "ADD_MESSAGE",
          payload: {
            ...payloadMessage,
            isRecoveryPlaceholder: false,
          },
        });
        scrollToBottom();
      }

      if (
        data.action === "update" &&
        Number(payloadTicketId) === Number(currentTicketId.current)
      ) {
        dispatch({ type: "UPDATE_MESSAGE", payload: payloadMessage });
      }
      if (
        data.action === "updateOperationalStatus" &&
        Number(payloadTicketId) === Number(currentTicketId.current)
      ) {
        dispatch({ type: "UPDATE_MESSAGE", payload: payloadMessage });
      }
    };

    socket.on(`company-${companyId}-appMessage`, handleAppMessage);

    return () => {
      socket.off(`company-${companyId}-appMessage`, handleAppMessage);
      if (activeTicketId) {
        socket.emit("leaveChatBox", `${activeTicketId}`);
      }
    };
  }, [ticketId, socketManager]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage({
      ...message,
      isOwnMessage: resolveIsOwnMessage(message),
      canEdit: canEditMessage(message),
    });
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const parseMessageData = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const getChatvixMessageMeta = (message) => {
    const parsed = parseMessageData(message?.dataJson);
    const meta = parsed?.__chatvixMeta;
    if (!meta || typeof meta !== "object") {
      return {
        externalFromMe: false,
        externalFromMeSource: null,
      };
    }

    return {
      externalFromMe: meta.externalFromMe === true,
      externalFromMeSource: meta.externalFromMeSource || null,
    };
  };

  const isReactionMessage = (message) => {
    const mediaType = String(message?.mediaType || "");
    return mediaType.toLowerCase().includes("reaction");
  };

  const resolveEditableMessageKind = (message) => {
    if (!message || message.isDeleted) return null;

    const mediaType = String(message.mediaType || "")
      .trim()
      .toLowerCase();
    if (
      mediaType.includes("reaction") ||
      [
        "audio",
        "sticker",
        "location",
        "locationmessage",
        "vcard",
        "contact",
        "contactmessage",
        "call_log",
        "product",
      ].includes(mediaType)
    ) {
      return null;
    }

    if (
      ["image", "video", "application", "document", "documentmessage"].includes(
        mediaType,
      )
    ) {
      return message.mediaUrl ? "caption" : null;
    }

    return "text";
  };

  const canEditMessage = (message) => {
    if (!allowMessageEditing) return false;
    if (!resolveIsOwnMessage(message)) return false;
    return Boolean(resolveEditableMessageKind(message));
  };

  const isExternalFromMeMessage = (message) => {
    if (!resolveIsOwnMessage(message)) return false;

    const parsed = parseMessageData(message?.dataJson);
    const meta = getChatvixMessageMeta(message);
    if (meta.externalFromMe) return true;

    return parsed?.key?.fromMe === false && Boolean(message?.fromMe);
  };

  const unwrapMessageContent = (payload) => {
    let content = payload?.message || payload;
    for (let i = 0; i < 8 && content; i++) {
      if (content?.ephemeralMessage?.message) {
        content = content.ephemeralMessage.message;
        continue;
      }
      if (content?.viewOnceMessage?.message) {
        content = content.viewOnceMessage.message;
        continue;
      }
      if (content?.viewOnceMessageV2?.message) {
        content = content.viewOnceMessageV2.message;
        continue;
      }
      if (content?.viewOnceMessageV2Extension?.message) {
        content = content.viewOnceMessageV2Extension.message;
        continue;
      }
      break;
    }
    return content;
  };

  const extractForwardingInfo = (message) => {
    const parsed = parseMessageData(message?.dataJson);
    const normalizedForwarding = parsed?.forwardingInfo;

    if (normalizedForwarding) {
      const score = Number(normalizedForwarding?.forwardingScore);
      const forwardingScore =
        Number.isFinite(score) && score >= 0 ? score : null;
      const isForwarded =
        normalizedForwarding?.isForwarded === true ||
        (forwardingScore ?? 0) > 0;

      return {
        isForwarded,
        forwardingScore,
        forwardedManyTimes:
          normalizedForwarding?.forwardedManyTimes === true ||
          (forwardingScore ?? 0) >= FORWARDED_MANY_TIMES_THRESHOLD,
      };
    }

    const collectContextInfo = (node, depth = 0) => {
      if (!node || typeof node !== "object" || depth > 8) return [];
      const contexts = [];

      Object.values(node).forEach((payload) => {
        if (!payload || typeof payload !== "object") return;
        const contextInfo = payload?.contextInfo;
        if (contextInfo && typeof contextInfo === "object") {
          contexts.push(contextInfo);
          if (contextInfo?.quotedMessage) {
            contexts.push(
              ...collectContextInfo(contextInfo.quotedMessage, depth + 1),
            );
          }
        }
      });

      return contexts;
    };

    const content = unwrapMessageContent(parsed);
    const contexts = collectContextInfo(content || {});

    let isForwarded = false;
    let forwardingScore = null;

    contexts.forEach((contextInfo) => {
      const score = Number(contextInfo?.forwardingScore);
      const hasScore = Number.isFinite(score) && score >= 0;
      const contextForwarded =
        contextInfo?.isForwarded === true || (hasScore && score > 0);
      isForwarded = isForwarded || contextForwarded;
      if (hasScore) {
        forwardingScore =
          forwardingScore === null ? score : Math.max(forwardingScore, score);
      }
    });

    return {
      isForwarded,
      forwardingScore,
      forwardedManyTimes:
        (forwardingScore ?? 0) >= FORWARDED_MANY_TIMES_THRESHOLD,
    };
  };

  const renderForwardedBadge = (message) => {
    const forwardingInfo = extractForwardingInfo(message);
    if (!forwardingInfo?.isForwarded) return null;

    const label = forwardingInfo.forwardedManyTimes
      ? "Encaminhada muitas vezes"
      : "Encaminhada";

    return <span className={classes.forwardedBadge}>{label}</span>;
  };

  const extractReactionInfo = (message) => {
    if (!isReactionMessage(message)) return null;
    const parsed = parseMessageData(message?.dataJson);
    const content = unwrapMessageContent(parsed);
    const reactionMessage =
      content?.reactionMessage ||
      parsed?.message?.reactionMessage ||
      parsed?.reactionMessage;
    const targetMessageId = reactionMessage?.key?.id;
    const emoji = reactionMessage?.text || message?.body;
    if (!targetMessageId || !emoji) return null;
    return {
      targetMessageId,
      emoji,
    };
  };

  const reactionsByMessageId = useMemo(() => {
    const map = new Map();
    messagesList.forEach((message) => {
      const reaction = extractReactionInfo(message);
      if (!reaction) return;
      const senderKey = message.fromMe
        ? "me"
        : String(
            message.contactId ||
              message.participant ||
              message.contact?.id ||
              message.id,
          );
      const current = map.get(reaction.targetMessageId) || new Map();
      current.set(senderKey, reaction.emoji);
      map.set(reaction.targetMessageId, current);
    });
    return map;
  }, [messagesList]);

  const visibleMessages = useMemo(
    () => messagesList.filter((message) => !isReactionMessage(message)),
    [messagesList],
  );

  const formatBytes = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return null;
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    const fixed = i === 0 ? 0 : v < 10 ? 2 : 1;
    return `${v.toFixed(fixed)} ${units[i]}`;
  };

  const safeDecode = (s) => {
    try {
      return decodeURIComponent(String(s || ""));
    } catch {
      return String(s || "");
    }
  };

  const extractFileNameFromUrl = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url, window.location.origin);
      const base = u.pathname.split("/").filter(Boolean).pop() || "";
      return safeDecode(base);
    } catch {
      const base =
        String(url).split("?")[0].split("#")[0].split("/").pop() || "";
      return safeDecode(base);
    }
  };

  const getDocumentMeta = (message) => {
    // 1) campos diretos no model (se existirem)
    const directName =
      message?.mediaName ||
      message?.originalName ||
      message?.fileName ||
      message?.filename ||
      "";

    const directSize =
      message?.mediaSize || message?.fileSize || message?.size || null;

    const directMime = message?.mimetype || message?.mimeType || null;

    // 2) tenta extrair do dataJson (Baileys)
    const parsed = parseMessageData(message?.dataJson);
    const content = unwrapMessageContent(parsed);
    const doc = content?.documentMessage;

    const jsonName = doc?.fileName || "";
    const jsonSize = doc?.fileLength || null;
    const jsonMime = doc?.mimetype || null;

    // 3) fallback: URL
    const urlName = extractFileNameFromUrl(message?.mediaUrl);

    const name = String(directName || jsonName || urlName || "").trim();
    const size = directSize ?? jsonSize ?? null;
    const mime = directMime || jsonMime || null;

    return {
      name: name || "",
      sizeText: formatBytes(size),
      mime: mime ? String(mime) : null,
    };
  };

  const getMimeFromAudioUrl = (url) => {
    const fileName = extractFileNameFromUrl(url).toLowerCase();
    const ext = fileName.split(".").pop();
    if (!ext || ext === fileName) return null;

    const mimeByExtension = {
      aac: "audio/aac",
      amr: "audio/amr",
      m4a: "audio/mp4",
      mp3: "audio/mpeg",
      oga: "audio/ogg",
      ogg: "audio/ogg",
      opus: "audio/ogg; codecs=opus",
      wav: "audio/wav",
      weba: "audio/webm",
      webm: "audio/webm",
    };

    return mimeByExtension[ext] || null;
  };

  const getAudioSources = (message) => {
    const parsed = parseMessageData(message?.dataJson);
    const content = unwrapMessageContent(parsed);
    const audio = content?.audioMessage;

    const directMime = message?.mimetype || message?.mimeType || null;
    const originalMime =
      audio?.mimetypeOriginal ||
      audio?.originalMimetype ||
      audio?.mediaMimetype ||
      null;
    const audioMime = audio?.mimetype || null;
    const extensionMime = getMimeFromAudioUrl(message?.mediaUrl);

    const candidates = [originalMime, directMime, audioMime, extensionMime]
      .filter(Boolean)
      .map((mime) => String(mime).trim())
      .filter(Boolean);

    // fallback progressivo: quando houver MIME original, oferece alternativas
    if (originalMime) {
      const normalizedOriginal = String(originalMime).split(";")[0].trim();
      if (normalizedOriginal && normalizedOriginal !== originalMime) {
        candidates.push(normalizedOriginal);
      }
      if (normalizedOriginal === "audio/ogg") {
        candidates.push("audio/ogg; codecs=opus", "audio/opus");
      }
      if (normalizedOriginal === "audio/webm") {
        candidates.push("audio/webm; codecs=opus");
      }
    }

    return [...new Set(candidates)].map((type) => ({
      src: message?.mediaUrl,
      type,
    }));
  };

  const getTicketRouteParam = (t) => {
    // Seu backend está buscando por Ticket.uuid (vide ShowTicketFromUUIDService).
    // Então a rota /tickets/:param tem que receber UUID, não id numérico.
    const u = t?.uuid;
    return u ? String(u) : "";
  };

  const handleTicketCreatedFromVcard = (createdTicket) => {
    const routeParam = getTicketRouteParam(createdTicket);
    if (!routeParam) return;
    history.push(`/tickets/${routeParam}`);
  };

  const getTicketWhatsappId = () => {
    return ticket?.whatsappId ?? ticket?.whatsapp?.id ?? null;
  };

  const getLocationPreviewData = (message) => {
    const parsed = parseMessageData(message?.dataJson);
    const content = unwrapMessageContent(parsed);
    const locationContent =
      content?.locationMessage || content?.liveLocationMessage;

    if (locationContent) {
      const latitude = locationContent?.degreesLatitude;
      const longitude = locationContent?.degreesLongitude;
      const hasCoordinates =
        latitude !== undefined &&
        latitude !== null &&
        longitude !== undefined &&
        longitude !== null;

      return {
        latitude,
        longitude,
        name: locationContent?.name || null,
        address: locationContent?.address || null,
        link: hasCoordinates
          ? `https://maps.google.com/?q=${latitude},${longitude}`
          : null,
      };
    }

    if (!message?.body) return null;
    const locationParts = message.body.split("|");
    if (locationParts.length < 2) return null;

    const [imageLocation, linkLocation, descriptionLocation] = locationParts;

    return {
      image: imageLocation,
      link: linkLocation,
      description: descriptionLocation || null,
    };
  };

  const getTemplateButtonData = (button) => {
    const label =
      button?.quickReplyButton?.displayText ||
      button?.urlButton?.displayText ||
      button?.callButton?.displayText ||
      button?.buttonText?.displayText ||
      "";
    const id =
      button?.quickReplyButton?.id ||
      button?.buttonId ||
      button?.urlButton?.url ||
      button?.callButton?.phoneNumber ||
      "";
    return { label, id };
  };

  const getInteractiveContent = (message) => {
    const parsed = parseMessageData(message?.dataJson);
    if (!parsed) return null;

    const content = unwrapMessageContent(parsed);
    if (!content) return null;

    if (content?.buttonsMessage) {
      const buttons =
        content.buttonsMessage.buttons
          ?.map((button) => ({
            id: button?.buttonId || "",
            label: button?.buttonText?.displayText || "",
          }))
          .filter((button) => button.label || button.id) || [];

      return {
        kind: "buttons",
        bodyText: content.buttonsMessage.contentText || message.body,
        items: buttons,
      };
    }

    if (content?.listMessage) {
      const rows = [];
      for (const section of content.listMessage.sections || []) {
        for (const row of section.rows || []) {
          if (row?.title || row?.rowId) {
            rows.push({
              id: row?.rowId || "",
              label: row.title || "",
              description: row.description || "",
            });
          }
        }
      }

      return {
        kind: "list",
        bodyText:
          content.listMessage.description ||
          content.listMessage.title ||
          message.body,
        items: rows,
      };
    }

    const template =
      content?.templateMessage?.hydratedTemplate ||
      content?.templateMessage?.hydratedFourRowTemplate;

    if (template?.hydratedButtons?.length) {
      const buttons = template.hydratedButtons
        .map((button) => getTemplateButtonData(button))
        .filter((button) => button.label || button.id);

      return {
        kind: "template",
        bodyText:
          template.hydratedContentText ||
          template.hydratedTitleText ||
          message.body,
        items: buttons,
      };
    }

    return null;
  };

  const handleInteractiveReply = async (item) => {
    const replyBody = item?.id || item?.label;
    if (!replyBody) return;

    try {
      await api.post(`/messages/${ticketId}`, {
        read: 1,
        fromMe: true,
        body: replyBody,
      });
    } catch (error) {
      toastError(error);
    }
  };

  /**
   * Handler para seleção de opção de menu de fila
   * Envia o código da opção como mensagem
   */
  const handleQueueMenuSelection = async (selectedCode) => {
    if (!selectedCode) return;

    try {
      console.log("[QueueMenu] Opção selecionada:", {
        ticketId,
        selectedCode,
        timestamp: new Date().toISOString(),
      });

      await api.post(`/messages/${ticketId}`, {
        read: 1,
        fromMe: true,
        body: selectedCode,
      });
    } catch (error) {
      toastError(error);
      console.error("[QueueMenu] Erro ao enviar seleção:", error);
    }
  };

  const renderInteractiveContent = (interactive) => {
    if (!interactive || !interactive.items?.length) return null;

    if (interactive.kind === "list") {
      return (
        <div className={classes.interactiveContainer}>
          {interactive.items.map((row, index) => (
            <button
              type="button"
              className={classes.listRow}
              key={`${row.label || row.id}-${index}`}
              onClick={() => handleInteractiveReply(row)}
            >
              <span className={classes.listRowTitle}>{row.label}</span>
              {row.description && (
                <span className={classes.listRowDescription}>
                  {row.description}
                </span>
              )}
            </button>
          ))}
        </div>
      );
    }

    return (
      <div className={classes.interactiveContainer}>
        {interactive.items.map((button, index) => (
          <Button
            key={`${button.label || button.id}-${index}`}
            variant="outlined"
            size="small"
            className={classes.interactiveButton}
            onClick={() => handleInteractiveReply(button)}
          >
            {button.label || button.id}
          </Button>
        ))}
      </div>
    );
  };

  const splitSignedMessageBody = (value) => {
    const text = String(value || "").replace(/\r\n/g, "\n");
    const match = text.match(/^(\*[^*\n]+:\*)\n+([\s\S]*)$/);

    if (!match) {
      return {
        signature: null,
        body: text,
      };
    }

    return {
      signature: match[1],
      body: match[2],
    };
  };

  /**
   * Parsing de opções de menu de fila
   * Padrão esperado: *[ codigo ]* - Label
   * Exemplo: *[ 1 ]* - Financeiro
   */
  const parseQueueMenuOptions = (messageBody) => {
    if (!messageBody || typeof messageBody !== "string") return null;
    
    // Regex para capturar padrão: *[ X ]* - Label
    // Mais permissivo para espaços
    const regex = /\*\[\s*([^\]]+)\s*\]\*\s*-\s*(.+?)(?=\n|$)/g;
    const options = [];
    let match;
    
    while ((match = regex.exec(messageBody)) !== null) {
      const code = match[1].trim();
      const label = match[2].trim();
      
      // Validar que código é válido (número, # ou !)
      if (code && label) {
        options.push({ code, label });
      }
    }
    
    return options.length > 0 ? options : null;
  };

  const renderMessageBodyContent = (message) => {
    const interactive = getInteractiveContent(message);
    const messageBody =
      message.mediaType === "locationMessage" ||
      message.mediaType === "location"
        ? null
        : interactive?.bodyText || message.body;
    const parsedMessageData = parseMessageData(message?.dataJson);
    const unwrappedMessageData = unwrapMessageContent(parsedMessageData);
    const bodyFromDataJson =
      unwrappedMessageData?.conversation ||
      unwrappedMessageData?.extendedTextMessage?.text ||
      unwrappedMessageData?.imageMessage?.caption ||
      unwrappedMessageData?.videoMessage?.caption ||
      unwrappedMessageData?.documentMessage?.caption ||
      "";

    const shouldRenderEditedStack =
      messageBody !== null &&
      !message.isDeleted &&
      Boolean(message.isEdited && message.editedOriginalBody);

    const originalParts = splitSignedMessageBody(message.editedOriginalBody);
    const finalParts = splitSignedMessageBody(messageBody);
    const shouldCollapseSignature =
      shouldRenderEditedStack &&
      resolveIsOwnMessage(message) &&
      Boolean(originalParts.signature) &&
      originalParts.signature === finalParts.signature;

    const displayedSignature = shouldCollapseSignature
      ? finalParts.signature
      : null;
    const displayedOriginalBody =
      shouldCollapseSignature && String(originalParts.body || "").trim()
        ? originalParts.body
        : message.editedOriginalBody;
    const displayedFinalBody =
      shouldCollapseSignature && String(finalParts.body || "").trim()
        ? finalParts.body
        : messageBody;
    const deletedSourceBody =
      String(message.body || "").trim() ||
      String(message.editedOriginalBody || "").trim() ||
      String(bodyFromDataJson || "").trim();
    const deletedParts = splitSignedMessageBody(deletedSourceBody);

    if (message.isDeleted) {
      if (resolveIsOwnMessage(message)) {
        return (
          <>
            {deletedParts.signature && (
              <div className={classes.editedSignature}>
                <MarkdownWrapper>{deletedParts.signature}</MarkdownWrapper>
              </div>
            )}
            {String(deletedParts.body || "").trim() && (
              <div className={classes.editedOriginalBody}>
                <MarkdownWrapper>{deletedParts.body}</MarkdownWrapper>
              </div>
            )}
            <div className={classes.deletedOwnContent}>
              <Block
                fontSize="small"
                className={clsx(classes.deletedIcon, classes.deletedOwnIcon)}
              />
              <span>{i18n.t("messagesList.deletedByMe")}</span>
            </div>
          </>
        );
      }

      return null;
    }

    // NOVO: Detectar e renderizar menu de filas
    const queueMenuOptions = parseQueueMenuOptions(messageBody);
    if (queueMenuOptions && queueMenuOptions.length > 0 && !resolveIsOwnMessage(message)) {
      return (
        <QueueMenuOptions
          options={queueMenuOptions}
          onSelect={(code) => handleQueueMenuSelection(code)}
          title={i18n.t("messagesList.selectQueueOption") || "Selecione uma opção:"}
        />
      );
    }

    return (
      <>
        {messageBody !== null &&
          (shouldRenderEditedStack ? (
            <div className={classes.editedBodyStack}>
              {displayedSignature && (
                <div className={classes.editedSignature}>
                  <MarkdownWrapper>{displayedSignature}</MarkdownWrapper>
                </div>
              )}
              <div className={classes.editedOriginalBody}>
                <MarkdownWrapper>{displayedOriginalBody}</MarkdownWrapper>
              </div>
              <div className={classes.editedFinalBody}>
                <MarkdownWrapper>{displayedFinalBody}</MarkdownWrapper>
              </div>
            </div>
          ) : (
            <MarkdownWrapper>{messageBody}</MarkdownWrapper>
          ))}
        {renderInteractiveContent(interactive)}
      </>
    );
  };

  const handleConversarFromVcard = (payload) => {
    const number = String(payload?.phone || payload?.phoneRaw || "").replace(
      /\D+/g,
      "",
    );
    if (!number) return;

    setVcardInitialContact({
      // SEM id -> NewTicketModal vai criar ticket direto por number
      number,
      name: payload?.name || payload?.displayName || "",
    });
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (createdTicket) => {
    setNewTicketModalOpen(false);
    setVcardInitialContact(null);
    // Se o modal fechou porque criou/abriu um ticket, navega pra ele
    if (createdTicket?.id) {
      handleTicketCreatedFromVcard(createdTicket);
    }
  };

  const checkMessageMedia = (message) => {
    if (
      message.mediaType === "locationMessage" ||
      message.mediaType === "location"
    ) {
      const locationPreview = getLocationPreviewData(message);

      if (locationPreview) {
        return <LocationPreview {...locationPreview} />;
      }
    } else if (message.mediaType === "contact") {
      const parsed = parseMessageData(message?.dataJson);
      const content = unwrapMessageContent(parsed);
      const contacts = [];

      if (content?.contactMessage) {
        contacts.push({
          displayName: content.contactMessage.displayName,
          vcard: content.contactMessage.vcard,
        });
      }

      if (content?.contactsArrayMessage?.contacts?.length) {
        content.contactsArrayMessage.contacts.forEach((contact) => {
          contacts.push({
            displayName: contact?.displayName,
            vcard: contact?.vcard,
          });
        });
      }

      if (!contacts.length) return null;

      return (
        <VcardPreview
          contacts={contacts}
          companyId={ticket?.companyId}
          whatsappId={ticket?.whatsappId}
          onConversar={handleConversarFromVcard}
        />
      );
    } else if (message.mediaType === "image") {
      /* else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0].number} />
    } */
      /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      const audioSources = getAudioSources(message);
      return (
        <audio controls src={message.mediaUrl}>
          {audioSources.map((source, index) => (
            <source
              key={`${message.id || message.mediaUrl}-${source.type}-${index}`}
              src={source.src}
              type={source.type}
            ></source>
          ))}
        </audio>
      );
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      const isDocument =
        message.mediaType === "application" ||
        message.mediaType === "document" ||
        message.mediaType === "documentMessage";

      const docMeta = isDocument ? getDocumentMeta(message) : null;
      return (
        <>
          {isDocument &&
            (docMeta?.name || docMeta?.sizeText || docMeta?.mime) && (
              <div className={classes.documentMetadata}>
                <div className={classes.documentName}>
                  {docMeta?.name || "Arquivo"}
                </div>
                {(docMeta?.sizeText || docMeta?.mime) && (
                  <div className={classes.documentDetails}>
                    {[docMeta?.sizeText, docMeta?.mime]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>
                )}
              </div>
            )}
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              {i18n.t("messagesList.header.buttons.download")}
            </Button>
          </div>
          <div style={{ marginBottom: message.body === "" ? 8 : 0 }}>
            <Divider />
          </div>
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    const operationalStatus = String(message?.operationalStatus || "").trim();
    const ack = Number(message?.ack || 0);

    const unconfirmed = operationalStatus === "failed_delivery_timeout" && ack < 2;

    let deliveryLabel = "pendente";
    if (unconfirmed) {
      deliveryLabel = "não confirmado";
    } else if (operationalStatus === "resent") {
      deliveryLabel = "reenviado";
    } else if (ack >= 4) {
      deliveryLabel = "lida";
    } else if (ack >= 3) {
      deliveryLabel = "entregue";
    } else if (ack >= 2) {
      deliveryLabel = "enviada";
    }

    const shouldShowResend = unconfirmed && resolveIsOwnMessage(message) && Boolean(message?.mediaUrl);

    const resendBusy = Boolean(resendingByMessageId[message.id]);

    const resendButton = shouldShowResend ? (
      <Tooltip title="Reenviar mídia">
        <span>
          <IconButton
            size="small"
            className={classes.resendButton}
            disabled={resendBusy}
            onClick={async () => {
              if (
                !window.confirm(
                  "A entrega não foi confirmada. Reenviar a mídia pode duplicar o envio ao cliente. Confirmar?"
                )
              ) {
                return;
              }
              try {
                setResendingByMessageId((prev) => ({
                  ...prev,
                  [message.id]: true,
                }));
                await api.post(`/messages/${message.id}/resend`);
              } catch (error) {
                toastError(error);
              } finally {
                setResendingByMessageId((prev) => ({
                  ...prev,
                  [message.id]: false,
                }));
              }
            }}
          >
            {resendBusy ? (
              <CircularProgress size={14} />
            ) : (
              <Replay className={classes.resendIcon} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    ) : null;

    let ackIcon = null;
    if (message.ack === 1) {
      ackIcon = <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      ackIcon = <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3) {
      ackIcon = <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 4 || message.ack === 5) {
      ackIcon = <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }

    if (operationalStatus === "failed_delivery_timeout") {
      ackIcon = (
        <ErrorOutline
          fontSize="small"
          color="error"
          className={classes.ackIcons}
        />
      );
    }

    return (
      <span className={classes.ackStatusRow}>
        <Tooltip title={`Status: ${deliveryLabel}`}>
          <span>
            {ackIcon || (
              <AccessTime fontSize="small" className={classes.ackIcons} />
            )}
          </span>
        </Tooltip>
        <span className={classes.deliveryStatusText}>{deliveryLabel}</span>
        {resendButton}
      </span>
    );
  };

  const renderDailyTimestamps = (message, index, messages) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(getMessageDate(messages[index]), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messages.length - 1) {
      let messageDay = getMessageDate(messages[index]);
      let previousMessageDay = getMessageDate(messages[index - 1]);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(getMessageDate(messages[index]), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messages.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderNumberTicket = (message, index, messages) => {
    if (index < messages.length && index > 0) {
      let messageTicket = message.ticketId;
      let previousMessageTicket = messages[index - 1].ticketId;

      if (messageTicket !== previousMessageTicket) {
        return (
          <center>
            <div className={classes.ticketNunberClosed}>
              Conversa encerrada:{" "}
              {format(
                getMessageDate(messages[index - 1]),
                "dd/MM/yyyy HH:mm:ss",
              )}
            </div>

            <div className={classes.ticketNunberOpen}>
              Conversa iniciada:{" "}
              {format(getMessageDate(message), "dd/MM/yyyy HH:mm:ss")}
            </div>
          </center>
        );
      }
    }
  };

  const renderMessageDivider = (message, index, messages) => {
    if (index < messages.length && index > 0) {
      let messageUser = resolveIsOwnMessage(messages[index]);
      let previousMessageUser = resolveIsOwnMessage(messages[index - 1]);
      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const formatParticipant = (participant) =>
    participant ? participant.replace(/@.+$/, "") : "";

  const getParticipantName = (message) => {
    const contactName = message?.participantContact?.name;
    if (contactName && !contactName.includes("@")) {
      return contactName;
    }
    // Socket-delivered messages do not carry participantContact yet (it is only
    // resolved by ListMessagesService). Prefer the canonical PN over the raw LID
    // so the chat never shows a LID as the participant label.
    const participantCanonical = String(message?.participantCanonical || "");
    const canonicalPn =
      participantCanonical && !participantCanonical.endsWith("@lid")
        ? formatParticipant(participantCanonical)
        : "";
    return (
      message?.participantPnFallback ||
      canonicalPn ||
      formatParticipant(contactName) ||
      formatParticipant(message?.participant)
    );
  };

  const resolveIsOwnMessage = (message) => {
    if (!message) return false;
    const isInternal = ticket?.isInternal ?? Boolean(message?.userId);
    if (isInternal) {
      if (!message?.userId || !currentUser?.id) {
        return false;
      }
      return Number(message.userId) === Number(currentUser.id);
    }
    return Boolean(message.fromMe);
  };

  const renderQuotedMessage = (message) => {
    const isOwnMessage = resolveIsOwnMessage(message);
    const isOwnQuotedMessage = resolveIsOwnMessage(message?.quotedMsg);
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: isOwnMessage,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: isOwnQuotedMessage,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!isOwnQuotedMessage && (
            <span className={classes.messageContactName}>
              {isGroup
                ? getParticipantName(message.quotedMsg)
                : message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio" && (
            <div className={classes.downloadMedia}>
              <audio controls>
                <source
                  src={message.quotedMsg.mediaUrl}
                  type="audio/ogg"
                ></source>
              </audio>
            </div>
          )}
          {message.quotedMsg.mediaType === "video" && (
            <video
              className={classes.messageMedia}
              src={message.quotedMsg.mediaUrl}
              controls
            />
          )}
          {message.quotedMsg.mediaType === "application" && (
            <div className={classes.downloadMedia}>
              <Button
                startIcon={<GetApp />}
                color="primary"
                variant="outlined"
                target="_blank"
                href={message.quotedMsg.mediaUrl}
              >
                {i18n.t("messagesList.header.buttons.download")}
              </Button>
            </div>
          )}

          {message.quotedMsg.mediaType === "image" && (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
          )}

          {message.quotedMsg.mediaType === "contactMessage" && (
            <span>{message.quotedMsg.body}</span>
          )}
          {!message.quotedMsg.mediaType ||
          ![
            "audio",
            "video",
            "application",
            "image",
            "contactMessage",
          ].includes(message.quotedMsg.mediaType) ? (
            <span>{message.quotedMsg.body}</span>
          ) : null}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (visibleMessages.length > 0) {
      const viewMessagesList = visibleMessages.map((message, index) => {
        const reactionsMap = reactionsByMessageId.get(message.id) || new Map();
        const reactionEntries = Array.from(reactionsMap.values()).reduce(
          (acc, emoji) => {
            const key = String(emoji || "").trim();
            if (!key) return acc;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          },
          {},
        );
        const hasReactions = Object.keys(reactionEntries).length > 0;
        const isOwnMessage = resolveIsOwnMessage(message);
        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index, visibleMessages)}
              {renderNumberTicket(message, index, visibleMessages)}
              {renderMessageDivider(message, index, visibleMessages)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {getParticipantName(message)}
                  </span>
                )}
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 17"
                    width="20"
                    height="17"
                  >
                    <path
                      fill="#df3333"
                      d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"
                    ></path>
                  </svg>{" "}
                  <span>
                    {i18n.t("messagesList.lostCall")}{" "}
                    {format(getMessageDate(message), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!isOwnMessage) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index, visibleMessages)}
              {renderNumberTicket(message, index, visibleMessages)}
              {renderMessageDivider(message, index, visibleMessages)}
              <div
                className={clsx(classes.messageLeft, {
                  [classes.messageWithReactions]: hasReactions,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {getParticipantName(message)}
                  </span>
                )}

                {/* aviso de mensagem apagado pelo contato */}
                {message.isDeleted && (
                  <div>
                    <span className={"message-deleted"}>
                      {i18n.t("messagesList.deletedMessage")} &nbsp;
                      <Block
                        color="error"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    </span>
                  </div>
                )}

                {(message.mediaUrl ||
                  message.mediaType === "locationMessage" ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard" ||
                  message.mediaType === "contact") &&
                  //|| message.mediaType === "multi_vcard"
                  checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {renderForwardedBadge(message)}
                  {renderMessageBodyContent(message)}
                  <span className={classes.timestamp}>
                    {message.isEdited && <span>Editada </span>}
                    {format(getMessageDate(message), "HH:mm")}
                  </span>
                </div>
                {hasReactions && (
                  <div className={classes.reactionContainer}>
                    {Object.entries(reactionEntries).map(([emoji, count]) => (
                      <span className={classes.reactionBadge} key={emoji}>
                        <span>{emoji}</span>
                        {count > 1 && (
                          <span className={classes.reactionCount}>{count}</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index, visibleMessages)}
              {renderNumberTicket(message, index, visibleMessages)}
              {renderMessageDivider(message, index, visibleMessages)}
              <div
                className={clsx(classes.messageRight, {
                  [classes.messageWithReactions]: hasReactions,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl ||
                  message.mediaType === "locationMessage" ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard" ||
                  message.mediaType === "contact") &&
                  //|| message.mediaType === "multi_vcard"
                  checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]:
                      message.isDeleted && !isOwnMessage,
                    [classes.textContentItemEdited]:
                      message.isEdited && Boolean(message.editedOriginalBody),
                  })}
                >
                  {isExternalFromMeMessage(message) && (
                    <div className={classes.externalFromMeHint}>
                      <span
                        role="img"
                        aria-label={i18n.t(
                          "messagesList.sentFromAnotherDevice",
                        )}
                      >
                        {"\uD83D\uDCF1"}
                      </span>
                      <span>
                        {i18n.t("messagesList.sentFromAnotherDevice")}
                      </span>
                    </div>
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {renderForwardedBadge(message)}
                  {renderMessageBodyContent(message)}
                  <span className={classes.timestamp}>
                    <span className={classes.timestampTimeRow}>
                      {message.isEdited && (
                        <span>{i18n.t("messagesList.edited")}</span>
                      )}
                      <span>{format(getMessageDate(message), "HH:mm")}</span>
                    </span>
                    <span className={classes.timestampAckRow}>
                      {renderMessageAck(message)}
                    </span>
                  </span>
                </div>
                {hasReactions && (
                  <div
                    className={clsx(classes.reactionContainer, {
                      [classes.reactionContainerRight]: isOwnMessage,
                    })}
                  >
                    {Object.entries(reactionEntries).map(([emoji, count]) => (
                      <span className={classes.reactionBadge} key={emoji}>
                        <span>{emoji}</span>
                        {count > 1 && (
                          <span className={classes.reactionCount}>{count}</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>{i18n.t("messagesList.saudation")}</div>;
    }
  };

  return (
    <div
      className={classes.messagesListWrapper}
      style={{ transform: `translateY(-${keyboardOffset}px)` }}
    >
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseNewTicketModal}
        initialContact={vcardInitialContact}
        contextCompanyId={ticket?.companyId}
        contextWhatsappId={ticket?.whatsappId}
      />
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
